import { Box, Button, Checkbox, FormField, Header, Input, Modal, RadioGroup, Select, SpaceBetween, Textarea } from '@amzn/awsui-components-react';
import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { DashboardChart, Partition } from '../../../common/DashboardLocalStorage';
import { constants } from '../../../common/Localization';
import { ChartFunctions, chartFunctions, ChartTypes, chartTypes, emptyPartition } from './constants';
import { CategoryOption } from './interfaces';
import PartitionTable from './Partition';

interface Props {
    isNew: boolean;
    existingChartData?: DashboardChart;
    isVisible: boolean;
    setVisible: (visible: boolean) => void;
    categoryOptions: CategoryOption[];
    saveChart: (chart: DashboardChart) => void;
}

function NewChartModal({ isNew, isVisible, setVisible, existingChartData, categoryOptions, saveChart }: Props): JSX.Element {

    const [ title, setTitle ] = useState('');
    const [ description, setDescription ] = useState('');
    const [ type, setType ] = useState<ChartTypes>();
    const [ category, setCategory ] = useState<CategoryOption | null>(null);
    const [ hasPartitionValues, setHasPartitionValues ] = useState(false);
    const [ partition, setPartition ] = useState<Partition>();
    const [ fn, setFn ] = useState<ChartFunctions | null>(null);
    const [ value, setValue ] = useState<CategoryOption | null>(null);

    useEffect(() => {
        if (!existingChartData) {
            setTitle('');
            setDescription('');
            setType(undefined);
            setCategory(null);
            setPartition(emptyPartition);
            setHasPartitionValues(false);
            setFn(null);
            setValue(null);
            return;
        }

        setTitle(existingChartData.title);
        setDescription(existingChartData.description);
        setType(existingChartData.type);
        let existingChartCategory: string = typeof existingChartData.category === 'string' ? existingChartData.category : existingChartData.category.prop;
        existingChartCategory = existingChartData.sourceName ? `${existingChartData.sourceName}:${existingChartCategory}` : existingChartCategory;
        const selectedCategory = categoryOptions.find(option => option.value === existingChartCategory);

        setCategory(selectedCategory ?? null);
        if (typeof existingChartData.category !== 'string' && existingChartData.category.partition) {
            setHasPartitionValues(true);
            setPartition(existingChartData.category.partition);
        } else {
            setHasPartitionValues(false);
            setPartition(emptyPartition);
        }
        const existingChartValue = existingChartData.sourceName ? 
            `${existingChartData.sourceName}:${existingChartData.value.prop}` : existingChartData.value.prop;
        const selectedValue = categoryOptions.find(option => option.value === existingChartValue);
        setValue(selectedValue ?? null);
        setFn(existingChartData.value.fn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ existingChartData, isVisible ]);

    const extractSourceName = (name: string | undefined): string | undefined => {
        const index = name ? name.indexOf(':') : -1;
        return index > 0 && name ? name.substring(0, index) : undefined;
    };

    const extractPropName = (name: string): string => {
        const index = name ? name.indexOf(':') : -1;
        return index > 0 && name ? name.substring(index + 1) : name;
    };

    const saveChartAndCloseModal = () => {
        if (!type || !category || !fn || !value) {
            return;
        }
        const categoryId = extractPropName(category.value);

        const chartToSave: DashboardChart = {
            id: existingChartData?.id ?? uuidv4(),
            type,
            title,
            description,
            withLabels: true,
            sourceName: extractSourceName(category.value),
            category: {
                prop: categoryId,
                partition: hasPartitionValues ? partition : undefined,
            },
            value: {
                fn,
                prop: extractPropName(value.value),
            },
        };
        saveChart(chartToSave);
        setVisible(false);
    };

    const isPartitionInvalid = (): boolean => {
        if (!hasPartitionValues) {
            return false;
        }
        if (partition?.labels.find(label => label.length === 0)) {
            return true;
        }
        return partition?.upperBounds.find(bound => isNaN(bound)) ? true : false;
    };

    const isSaveDisabled = (): boolean => {
        return isPartitionInvalid() || title.length === 0 || category === null || value === null || type === undefined || fn === null;
    };

    const setChartCategory = (selectedCategory: CategoryOption) => {
        const selectedCategoryValue = existingChartData?.sourceName ? 
            selectedCategory.value.substring(existingChartData.sourceName.length + 1) : selectedCategory.value;
        const selectedChartCategory = categoryOptions.find(option => option.value === selectedCategoryValue);
        setCategory(selectedChartCategory ?? null);
    };

    const setChartValue = (selectedValue: CategoryOption) => {
        const selectedOptionValue = existingChartData?.sourceName ? 
            selectedValue.value.substring(existingChartData.sourceName.length + 1) : selectedValue.value;
        const value = categoryOptions.find(option => option.value === selectedOptionValue);
        setValue(value ?? null);
    };

    const setChartType = (selectedValue: string) => {
        const selectedChartType = chartTypes.find(chartOption => chartOption.value === selectedValue);
        setType(selectedChartType?.value);
    };

    const setChartFn = (selectedValue: string) => {
        const selectedFn = chartFunctions.find(chartFn => chartFn.value === selectedValue);
        setFn(selectedFn?.value ?? null);
    };

    const header = (
        <Header>
            {isNew ? constants.ADD_BUTTON_TEXT : constants.EDIT_BUTTON_TEXT} {constants.CHART_LOWERCASE}
        </Header>
    );

    const footer = (
        <Box float='right'> 
            <SpaceBetween size='s' direction='horizontal'>
                <Button onClick={() => setVisible(false)} variant='link'>{constants.CANCEL}</Button>
                <Button onClick={saveChartAndCloseModal} disabled={isSaveDisabled()} variant='primary'
                >
                    {isNew ? constants.ADD_BUTTON_TEXT : constants.SAVE_BUTTON_TEXT}
                </Button>
            </SpaceBetween>
        </Box>
    );

    const formContent = (
        <SpaceBetween size='m'> 
            <FormField
                label={constants.TITLE}
                description={constants.TITLE_DESCRIPTION}
            >
                <Input
                    value={title ?? ''}
                    onChange={({ detail }) => setTitle(detail.value)}
                />
            </FormField>
            <FormField
                label={<span>{constants.DESCRIPTION} <i>- {constants.OPTIONAL}</i></span>}
                description={constants.CHART_DESCRIPTION}
            >
                <Textarea
                    value={description ?? ''}
                    onChange={({ detail }) => setDescription(detail.value)}
                />
            </FormField>
            <FormField
                label={constants.CHART_TYPE}
            >
                <RadioGroup
                    items={chartTypes}
                    value={type ?? null}
                    onChange={({ detail }) => setChartType(detail.value)}
                />
            </FormField>
            <FormField
                label={constants.CATEGORY}
            >
                <Select
                    options={categoryOptions}
                    selectedOption={category}
                    onChange={({ detail }) => setChartCategory(detail.selectedOption as CategoryOption)}
                />
                <Checkbox
                    checked={hasPartitionValues}
                    onChange={({ detail }) => setHasPartitionValues(detail.checked)}
                >{constants.PARTITION_VALUES}</Checkbox>
            </FormField>
            { hasPartitionValues && partition ? (
                <div >
                    <PartitionTable partition={partition} setPartition={setPartition}/>
                </div>
            ) : null}
            <FormField
                label={constants.FUNCTION}
            >
                <RadioGroup
                    items={chartFunctions}
                    value={fn}
                    onChange={({ detail }) => setChartFn(detail.value)}
                />
            </FormField>
            <FormField
                label={constants.VALUE}
            >
                <Select
                    options={categoryOptions}
                    selectedOption={value}
                    onChange={({ detail }) => setChartValue(detail.selectedOption as CategoryOption)}
                    placeholder={constants.CHOOSE_A_PROPERTY}
                />
            </FormField>
        </SpaceBetween>
    );

    return (
        <Modal
            visible={isVisible}
            onDismiss={() => setVisible(false)}
            header={header}
            footer={footer}
            size='large'
        >
            {formContent}
        </Modal>
    );
}

export default NewChartModal;
